/**
 * Cashfree Payment Integration Service
 * Handles payment processing for package purchases
 * Connected to Firebase Cloud Functions backend
 */

// Firebase Cloud Functions deployed URLs
const CASHFREE_FUNCTIONS = {
    createOrder: 'https://us-central1-shivai-50d37.cloudfunctions.net/createCashfreeOrder',
    createOrderTest: 'https://us-central1-shivai-50d37.cloudfunctions.net/createCashfreeOrderTest',
    verifyPayment: 'https://us-central1-shivai-50d37.cloudfunctions.net/verifyCashfreePayment',
};

// Determine if we're in test mode based on environment variable
// Set REACT_APP_CASHFREE_TEST_MODE=true in .env for temporary testing
// Set REACT_APP_CASHFREE_TEST_MODE=false or remove it for production
const IS_TEST_MODE = process.env.REACT_APP_CASHFREE_TEST_MODE === 'true';

console.log('🔑 [Cashfree Config] Mode:', IS_TEST_MODE ? '🧪 TEST/SANDBOX' : '🚀 PRODUCTION');
console.log('🔑 [Cashfree Config] Environment Variable:', process.env.REACT_APP_CASHFREE_TEST_MODE);

/**
 * Load Cashfree Checkout script dynamically
 * @returns {Promise<boolean>} Success status
 */
export const loadCashfreeScript = () => {
    return new Promise((resolve) => {
        // Check if script already loaded
        if (window.Cashfree) {
            resolve(true);
            return;
        }

        const script = document.createElement('script');
        script.src = 'https://sdk.cashfree.com/js/v3/cashfree.js';
        script.onload = () => {
            resolve(true);
        };
        script.onerror = () => {
            resolve(false);
        };
        document.body.appendChild(script);
    });
};

/**
 * Create Cashfree order via Firebase Cloud Function
 * @param {Object} orderData - Order details
 * @returns {Object} Order creation result
 */
export const createCashfreeOrder = async (orderData) => {
    try {
        console.log('💳 [createCashfreeOrder] Creating order for amount:', orderData.amount);
        console.log('🔐 [createCashfreeOrder] Calling Firebase Cloud Function');
        console.log('📌 [createCashfreeOrder] Test Mode:', IS_TEST_MODE);

        const functionUrl = IS_TEST_MODE ? CASHFREE_FUNCTIONS.createOrderTest : CASHFREE_FUNCTIONS.createOrder;

        const response = await fetch(functionUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                orderId: orderData.orderId,
                orderAmount: orderData.amount,
                orderCurrency: orderData.currency || 'INR',
                customerId: orderData.customerId,
                customerEmail: orderData.customerEmail,
                customerPhone: orderData.customerPhone,
            }),
        });

        // Get response body regardless of status
        const data = await response.json();
        console.log('📥 [createCashfreeOrder] Response:', data);

        if (!response.ok) {
            console.error('❌ [createCashfreeOrder] Server error:', {
                status: response.status,
                statusText: response.statusText,
                data,
            });
            throw new Error(`Failed to create order: ${data.details || data.error || response.status}`);
        }

        console.log('✅ [createCashfreeOrder] Order created via Firebase:', data);

        // Validate the response
        if (!data.orderId || !data.paymentSessionId) {
            console.error('❌ [createCashfreeOrder] Invalid order response:', data);
            return { success: false, error: 'Invalid order response from server' };
        }

        return { success: true, order: data };
    } catch (error) {
        console.error('❌ [createCashfreeOrder] Error:', error);
        return { success: false, error: error.message };
    }
};

/**
 * Initialize Cashfree payment
 * @param {Object} paymentData - Payment configuration
 * @returns {Promise<Object>} Payment result
 */
export const initiateCashfreePayment = async (paymentData) => {
    try {
        console.log('💳 [initiateCashfreePayment] Starting payment process...');

        const {
            amount,
            currency = 'INR',
            packageName,
            packageId,
            userEmail,
            userName,
            userPhone,
            employerName,
            employerPhone,
            onSuccess,
            onFailure,
        } = paymentData;

        console.log('💰 [initiateCashfreePayment] Amount:', amount, currency);
        console.log('📦 [initiateCashfreePayment] Package:', packageName);

        // Load Cashfree script
        console.log('📜 [initiateCashfreePayment] Loading Cashfree script...');
        const scriptLoaded = await loadCashfreeScript();
        if (!scriptLoaded) {
            console.error('❌ [initiateCashfreePayment] Failed to load Cashfree SDK');
            throw new Error('Failed to load Cashfree SDK. Please check your internet connection.');
        }
        console.log('✅ [initiateCashfreePayment] Cashfree script loaded');

        // Generate unique order ID
        const orderId = `order_${packageId}_${Date.now()}`;

        // Create order via Firebase Cloud Function
        console.log('📝 [initiateCashfreePayment] Creating order...');
        const orderResult = await createCashfreeOrder({
            orderId,
            amount,
            currency,
            customerId: userPhone || `user_${Date.now()}`,
            customerEmail: userEmail,
            customerPhone: userPhone,
        });

        if (!orderResult.success) {
            console.error('❌ [initiateCashfreePayment] Order creation failed');
            throw new Error('Failed to create payment order. Please try again.');
        }

        const order = orderResult.order;
        console.log('✅ [initiateCashfreePayment] Order created:', order.orderId);

        // Initialize Cashfree
        const cashfree = await window.Cashfree({
            mode: IS_TEST_MODE ? 'sandbox' : 'production',
        });

        // Cashfree checkout options - using redirect: false to handle inline
        const checkoutOptions = {
            paymentSessionId: order.paymentSessionId,
            redirectTarget: '_modal', // Keep payment in modal, don't redirect
        };

        console.log('🚀 [initiateCashfreePayment] Opening Cashfree checkout...');

        // Open Cashfree checkout and handle payment completion
        const paymentPromise = cashfree.checkout(checkoutOptions);

        // Handle payment result
        paymentPromise.then(async (result) => {
            console.log('💳 [Cashfree] Payment result received:', result);

            if (result.error) {
                // Payment failed
                console.error('❌ [Cashfree] Payment failed!');
                console.error('❌ [Cashfree] Error:', result.error);

                const failureResult = {
                    success: false,
                    error: result.error.message || 'Payment failed',
                    code: result.error.code,
                };

                console.log('📞 [Cashfree] Calling failure callback');
                if (onFailure) {
                    onFailure(failureResult);
                }
                return;
            }

            // Payment completed - verify with backend
            console.log('✅ [Cashfree] Payment completed, verifying...');

            // Wait a moment for Cashfree to process
            await new Promise(resolve => setTimeout(resolve, 2000));

            // Verify payment
            const verificationResult = await verifyCashfreePayment({
                orderId: order.orderId,
                testMode: IS_TEST_MODE,
            });

            console.log('🔍 [Cashfree] Verification result:', verificationResult);

            if (verificationResult.verified && verificationResult.isPaid) {
                console.log('✅ [Cashfree] Payment verified successfully');
                const paymentResult = {
                    success: true,
                    paymentId: order.orderId,
                    orderId: order.orderId,
                    packageId,
                    packageName,
                    amount: amount,
                    currency,
                    timestamp: new Date().toISOString(),
                    verified: true,
                    orderStatus: verificationResult.orderStatus,
                    paymentStatus: verificationResult.paymentStatus,
                };

                // Send WhatsApp message to employer if details provided
                if (employerName && employerPhone) {
                    console.log('📱 [Cashfree] Sending WhatsApp notification to employer');
                    const message = `Dear ${employerName},\n\nPayment of ₹${amount} for ${packageName} has been successfully received.\n\nPayment Details:\n- Order ID: ${order.orderId}\n- Amount: ₹${amount}\n- Package: ${packageName}\n- Date: ${new Date().toLocaleString('en-IN')}\n\nThank you for your business!\n\nBest regards,\nUdaan Jobs Team`;

                    // You can implement WhatsApp sending here if needed
                    console.log('📱 WhatsApp message:', message);
                }

                console.log('🎉 [Cashfree] Calling success callback with:', paymentResult);
                if (onSuccess) {
                    onSuccess(paymentResult);
                }
            } else {
                console.error('❌ [Cashfree] Payment verification failed');
                console.error('❌ [Cashfree] Verification details:', verificationResult);
                if (onFailure) {
                    onFailure({
                        success: false,
                        error: 'Payment verification failed. Please contact support.',
                        verified: false,
                        details: verificationResult,
                    });
                }
            }
        }).catch(async (error) => {
            console.error('❌ [Cashfree] Payment error:', error);

            // Even on error, try to verify the payment
            // Sometimes payment succeeds but SDK throws error
            try {
                await new Promise(resolve => setTimeout(resolve, 2000));
                const verificationResult = await verifyCashfreePayment({
                    orderId: order.orderId,
                    testMode: IS_TEST_MODE,
                });

                if (verificationResult.verified && verificationResult.isPaid) {
                    console.log('✅ [Cashfree] Payment verified successfully despite error');
                    const paymentResult = {
                        success: true,
                        paymentId: order.orderId,
                        orderId: order.orderId,
                        packageId,
                        packageName,
                        amount: amount,
                        currency,
                        timestamp: new Date().toISOString(),
                        verified: true,
                        orderStatus: verificationResult.orderStatus,
                        paymentStatus: verificationResult.paymentStatus,
                    };

                    if (onSuccess) {
                        onSuccess(paymentResult);
                    }
                    return;
                }
            } catch (verifyError) {
                console.error('❌ [Cashfree] Verification also failed:', verifyError);
            }

            // If verification also failed, call failure callback
            if (onFailure) {
                onFailure({
                    success: false,
                    error: error.message || 'Payment failed',
                    cancelled: error.message?.includes('cancel') || error.message?.includes('close'),
                });
            }
        });

        console.log('✅ [initiateCashfreePayment] Cashfree checkout initiated successfully');

        return { success: true, message: 'Payment initiated' };
    } catch (error) {
        console.error('❌ [initiateCashfreePayment] Error:', error);
        console.error('❌ [initiateCashfreePayment] Stack:', error.stack);
        return { success: false, error: error.message };
    }
};

/**
 * Verify Cashfree payment via Firebase Cloud Function
 * @param {Object} paymentData - Payment verification data
 * @returns {Promise<Object>} Verification result
 */
export const verifyCashfreePayment = async (paymentData) => {
    try {
        console.log('🔐 [verifyCashfreePayment] Verifying payment:', paymentData);
        console.log('🔐 [verifyCashfreePayment] Calling Firebase Cloud Function');

        const response = await fetch(CASHFREE_FUNCTIONS.verifyPayment, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                orderId: paymentData.orderId,
                testMode: paymentData.testMode || IS_TEST_MODE,
            }),
        });

        if (!response.ok) {
            throw new Error(`Verification failed: ${response.status}`);
        }

        const data = await response.json();
        console.log('✅ [verifyCashfreePayment] Payment verified via Firebase:', data);
        
        // Handle variations in status fields returned by cloud function or Cashfree API
        const isPaid = Boolean(
            data.isPaid === true || 
            data.orderStatus === 'PAID' || 
            data.paymentStatus === 'SUCCESS' || 
            data.order_status === 'PAID'
        );

        return {
            success: true,
            verified: isPaid,
            isPaid: isPaid,
            orderStatus: data.orderStatus || data.order_status || (isPaid ? 'PAID' : 'FAILED'),
            paymentStatus: data.paymentStatus || (isPaid ? 'SUCCESS' : 'FAILED'),
            orderAmount: data.orderAmount || data.order_amount,
            raw: data,
        };
    } catch (error) {
        console.error('❌ [verifyCashfreePayment] Error:', error);
        return { success: false, error: error.message, verified: false, isPaid: false };
    }
};

/**
 * Format amount for display
 * @param {number} amount - Amount in rupees
 * @param {string} currency - Currency code
 * @returns {string} Formatted amount
 */
export const formatAmount = (amount, currency = 'INR') => {
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(amount);
};

/**
 * Calculate GST (18% for digital services in India)
 * @param {number} amount - Base amount
 * @returns {Object} Amount breakdown with GST
 */
export const calculateGST = (amount) => {
    const gstRate = 0.18; // 18% GST
    const gstAmount = amount * gstRate;
    const totalAmount = amount + gstAmount;

    return {
        baseAmount: amount,
        gstRate: gstRate * 100,
        gstAmount: Math.round(gstAmount * 100) / 100,
        totalAmount: Math.round(totalAmount * 100) / 100,
    };
};

const cashfreeService = {
    loadCashfreeScript,
    createCashfreeOrder,
    initiateCashfreePayment,
    verifyCashfreePayment,
    formatAmount,
    calculateGST,
};

export default cashfreeService;
